import { Link } from "react-router-dom";
import SectionHeading from "@/components/feature/SectionHeading";
import { movieInfo, movieStillImages } from "@/mocks/movies";

const plotParagraphs = [
  "故事发生在动荡的上世纪四十年代。几位怀揣理想的热血青年，因为共同的信念走到了一起，他们密谋在一个纸醉金迷的都市里，完成一件足以改变命运的大事。",
  "为了接近目标人物，一位年轻女性被安排扮演一个全新的身份。她开始学习社交礼仪、熟悉上流社会的谈吐与举止，也渐渐习惯了一个本不属于自己的世界。身份的转换让她时刻紧绷，每一天都像走在刀刃之上。",
  "在与目标人物一次次周旋的过程中，两人之间产生了一种复杂而微妙的情感。这份情感既源于任务的需要，又在不知不觉间越过了预先划定的边界。她开始分不清，自己究竟是那个角色，还是角色背后的自己。",
  "与此同时，她与同伴们之间的关系也在悄然改变。最初的理想与激情，在漫长而危险的等待中逐渐被消磨，取而代之的是彼此的猜疑、牵挂与不舍。",
  "当所有线索最终汇聚的时刻到来，每个人都必须做出自己的选择。有人选择坚守，有人选择顺从内心，也有人，在最后一刻才明白自己真正想要的是什么。",
  "影片没有给出一个轻松明亮的答案，却用极为克制的镜头语言，把人物内心的每一次波动都清晰地留在观众心里。它讲述的不仅是一段故事，更是一代人在时代洪流中的挣扎与抉择。",
];

export default function Story() {
  return (
    <div className="w-full">
      <div className="relative w-full h-[280px] md:h-[380px] overflow-hidden bg-background-100">
        <img
          src="https://readdy.ai/api/search-image?query=cinematic%20wide%20film%20still%20of%20a%201930s%20Shanghai%20vintage%20cityscape%20at%20dusk%20with%20warm%20amber%20lights%20and%20deep%20crimson%20sky%2C%20moody%20dramatic%20period%20atmosphere%2C%20no%20text&width=1920&height=720&seq=story-hero-banner&orientation=landscape"
          alt="《色戒》剧情介绍"
          title="色戒 剧情详解"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/30" />
        <div className="absolute inset-0 w-full px-4 md:px-8 flex items-end pb-8 md:pb-12">
          <div className="w-full">
            <nav className="flex items-center gap-2 text-xs text-secondary-500 mb-3">
              <Link to="/" className="hover:text-accent-500 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">电影剧情介绍</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950">电影剧情介绍</h1>
            <p className="mt-3 text-sm md:text-base text-foreground-700 max-w-2xl">{movieInfo.slogan}</p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <article className="lg:col-span-2">
            <SectionHeading title="故事梗概" subtitle="一个关于身份、情感与抉择的时代故事" />

            <div className="mt-6 space-y-6">
              {plotParagraphs.map((text, i) => (
                <div key={i}>
                  <p className="text-sm md:text-[15px] leading-loose text-foreground-700">{text}</p>
                  {i === 1 && (
                    <figure className="my-8">
                      <div className="w-full aspect-video rounded-lg overflow-hidden bg-background-200">
                        <img
                          src={movieStillImages[0]}
                          alt="《色戒》剧照 - 时代街景"
                          title="色戒 剧照"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <figcaption className="mt-2.5 text-xs text-secondary-500 text-center">
                        剧照示意 · 时代氛围的细腻还原
                      </figcaption>
                    </figure>
                  )}
                  {i === 3 && (
                    <div className="my-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
                        <img
                          src={movieStillImages[1]}
                          alt="《色戒》剧照 - 室内场景"
                          title="色戒 剧照"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
                        <img
                          src={movieStillImages[2]}
                          alt="《色戒》剧照 - 建筑场景"
                          title="色戒 剧照"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-lg bg-background-100 border border-background-200 p-5 md:p-7">
              <div className="flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">制作背景</h2>
              </div>
              <p className="mt-4 text-sm md:text-[15px] leading-loose text-foreground-700">
                影片改编自同名短篇小说，由著名导演李安执导。为了还原故事发生的年代，剧组在美术、服装与道具上投入了大量精力，
                从建筑样式到一件旗袍的剪裁，都力求真实可信。影片上映后在国际影坛获得广泛关注，并斩获多项重要奖项，
                成为华语电影史上不可忽视的一部作品。
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {movieInfo.awards.map((award) => (
                  <div key={award} className="flex items-start gap-2.5 rounded-md bg-background-50 border border-background-200 p-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-500 text-primary-950 shrink-0 mt-0.5">
                      <i className="ri-trophy-line text-xs" />
                    </span>
                    <span className="text-sm text-foreground-800 leading-relaxed">{award}</span>
                  </div>
                ))}
              </div>
            </div>
          </article>

          <aside className="space-y-6">
            <div className="rounded-lg bg-background-100 border border-background-200 overflow-hidden">
              <div className="w-full aspect-[2/3] bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=minimalist%20cinematic%20movie%20poster%20composition%20of%20an%20elegant%20red%20curtain%20and%20warm%20lamp%20on%20dark%20background%2C%20deep%20crimson%20and%20amber%20tones%2C%20moody%20period%20style%2C%20no%20text&width=600&height=900&seq=story-poster&orientation=portrait"
                  alt="《色戒》海报"
                  title="色戒 在线观看"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <h3 className="font-heading text-lg font-bold text-foreground-950">影片基本信息</h3>
              <dl className="mt-4 divide-y divide-background-200">
                {movieInfo.facts.map((fact) => (
                  <div key={fact.label} className="flex items-start justify-between gap-4 py-3">
                    <dt className="text-sm text-secondary-500 shrink-0">{fact.label}</dt>
                    <dd className="text-sm text-foreground-900 text-right">{fact.value}</dd>
                  </div>
                ))}
                <div className="flex items-start justify-between gap-4 py-3">
                  <dt className="text-sm text-secondary-500 shrink-0">主演</dt>
                  <dd className="text-sm text-foreground-900 text-right">{movieInfo.cast}</dd>
                </div>
                <div className="flex items-start justify-between gap-4 py-3">
                  <dt className="text-sm text-secondary-500 shrink-0">评分</dt>
                  <dd className="text-sm text-accent-500 text-right font-medium">{movieInfo.rating} / 10</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg bg-primary-900 text-foreground-950 p-5">
              <h3 className="font-heading text-lg font-bold">准备好开始观看了吗？</h3>
              <p className="mt-2 text-xs text-primary-100/80 leading-relaxed">
                本站播放功能为前端演示，点击即可体验模拟播放界面。
              </p>
              <Link
                to="/watch"
                className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg" />
                前往播放页
              </Link>
            </div>

            <Link
              to="/"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-background-100 border border-background-200 text-foreground-800 text-sm hover:border-accent-500 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              返回首页
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
}